import React, { useState } from 'react';

const GrammarUnit9Content = ({ unitData, userPreferences, onMarkComplete, userProgress }) => {
  const [currentSection, setCurrentSection] = useState('overview');

  // Safety check for unit data
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/30 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">No Content Available</h2>
            <p className="text-gray-300">Content for Unit 9 is not available.</p>
          </div>
        </div>
      </div>
    );
  }

  const { content } = unitData;

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">動名詞概述</h3>
        
        <div className="space-y-4">
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold mb-2 text-green-300">形成方式</h4>
            <p className="text-yellow-300 font-mono mb-2">{content.overview.content.formation.content}</p>
            <p className="text-gray-300 text-sm">{content.overview.content.formation.note}</p>
          </div>

          <div className="bg-gray-700/50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold mb-2 text-green-300">四大功能</h4>
            <div className="space-y-2">
              {content.overview.content.functions.map((func, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-yellow-300 font-mono mr-2">{index + 1}.</span>
                  <span className="text-gray-300">{func}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActivities = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">表示活動的動名詞</h3>
        
        <div className="space-y-6">
          {content.activities.subsections.map((section, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{section.title}</h4>
              
              {section.content.activities && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                  {section.content.activities.map((activity, idx) => (
                    <div key={idx} className="bg-gray-600/30 p-2 rounded text-sm">
                      <span className="text-blue-300 font-semibold">{activity.english}</span>
                      <span className="text-gray-400 ml-2">- {activity.chinese}</span>
                    </div>
                  ))}
                </div>
              )}

              {section.content.explanation && (
                <p className="text-gray-300 mb-2">{section.content.explanation}</p>
              )}

              {section.content.note && (
                <p className="text-orange-300 text-sm mb-3">{section.content.note}</p>
              )}

              {section.content.examples && (
                <div className="space-y-2">
                  {section.content.examples.map((example, idx) => (
                    <div key={idx} className="text-sm">
                      <p className="text-blue-300">{example.english}</p>
                      <p className="text-gray-400">{example.chinese}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAdjectiveGerund = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">形容詞+介系詞+V-ing</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          {content.adjective_gerund.content.patterns.map((pattern, index) => (
            <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
              <span className="text-yellow-300 font-semibold">{pattern.phrase}</span>
              <span className="text-gray-300 ml-2">- {pattern.meaning}</span>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          {content.adjective_gerund.content.examples.map((example, index) => (
            <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
              <p className="text-blue-300">{example.english}</p>
              <p className="text-gray-400">{example.chinese}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderVerbPreposition = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">動詞+介系詞+V-ing</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg font-semibold mb-3 text-green-300">基本模式</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {content.verb_preposition.content.patterns.map((pattern, index) => (
                <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
                  <span className="text-yellow-300 font-semibold">{pattern.phrase}</span>
                  <span className="text-gray-300 ml-2">- {pattern.meaning}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3 text-green-300">特殊模式</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {content.verb_preposition.content.specialPatterns.map((pattern, index) => (
                <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
                  <span className="text-yellow-300 font-semibold">{pattern.phrase}</span>
                  <span className="text-gray-300 ml-2">- {pattern.meaning}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3 text-green-300">僅介系詞</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {content.verb_preposition.content.prepositionOnly.map((pattern, index) => (
                <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
                  <span className="text-yellow-300 font-semibold">{pattern.phrase}</span>
                  <span className="text-gray-300 ml-2">- {pattern.meaning}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3 text-green-300">例句</h4>
            <div className="space-y-2">
              {content.verb_preposition.content.examples.map((example, index) => (
                <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
                  <p className="text-blue-300">{example.english}</p>
                  <p className="text-gray-400">{example.chinese}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCommonVerbs = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">其他常見的動詞</h3>
        
        <div className="space-y-6">
          {content.common_verbs.subsections.map((section, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{section.title}</h4>
              
              {section.verbs && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {section.verbs.map((verb, idx) => (
                    <span key={idx} className="bg-blue-600/30 px-3 py-1 rounded text-sm">
                      {verb}
                    </span>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                {section.examples.map((example, idx) => (
                  <div key={idx} className="bg-gray-600/30 p-3 rounded">
                    <p className="text-blue-300">{example.english}</p>
                    <p className="text-gray-400">{example.chinese}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="bg-gray-800/30 rounded-xl p-4 mb-6">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCurrentSection('overview')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'overview'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          概覽
        </button>
        <button
          onClick={() => setCurrentSection('activities')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'activities'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          活動表達
        </button>
        <button
          onClick={() => setCurrentSection('adjective_gerund')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'adjective_gerund'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          形容詞+介系詞
        </button>
        <button
          onClick={() => setCurrentSection('verb_preposition')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'verb_preposition'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          動詞+介系詞
        </button>
        <button
          onClick={() => setCurrentSection('common_verbs')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'common_verbs'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          常見動詞
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2 text-blue-300">
            {unitData.title[userPreferences?.language || 'traditional']}
          </h1>
          <p className="text-gray-300 mb-4">
            {unitData.subtitle[userPreferences?.language || 'traditional']}
          </p>
          <p className="text-gray-400">
            {unitData.description[userPreferences?.language || 'traditional']}
          </p>
        </div>

        {/* Navigation */}
        {renderNavigation()}

        {/* Content */}
        {currentSection === 'overview' && renderOverview()}
        {currentSection === 'activities' && renderActivities()}
        {currentSection === 'adjective_gerund' && renderAdjectiveGerund()}
        {currentSection === 'verb_preposition' && renderVerbPreposition()}
        {currentSection === 'common_verbs' && renderCommonVerbs()}

        {/* Complete Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => onMarkComplete && onMarkComplete(unitData.id)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            標記為完成
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrammarUnit9Content;
